import { Injectable, NgZone, OnDestroy } from '@angular/core';

import { environment } from '../../environments/enviroment';

@Injectable()
abstract class ScopedWebSocketBaseService implements OnDestroy {
  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempt = 0;
  private manuallyDisconnected = true;
  private currentUserId: string | null = null;
  private currentToken: string | null = null;
  private currentUrl: string | null = null;

  protected constructor(
    private readonly channelName: string,
    protected readonly zone: NgZone
  ) {}

  connect(userId?: string | null, token?: string | null): void {
    const normalizedUserId = String(userId || '').trim();
    const normalizedToken = String(token || '').trim();

    if (!normalizedUserId || !normalizedToken) {
      this.disconnect();
      return;
    }

    const nextUrl = this.buildUrl(normalizedUserId, normalizedToken);
    const shouldReconnect =
      this.currentUrl !== nextUrl ||
      !this.socket ||
      (this.socket.readyState !== WebSocket.OPEN && this.socket.readyState !== WebSocket.CONNECTING);

    this.currentUserId = normalizedUserId;
    this.currentToken = normalizedToken;
    this.currentUrl = nextUrl;
    this.manuallyDisconnected = false;

    if (!shouldReconnect) {
      return;
    }

    this.clearReconnectTimer();
    this.closeSocket();
    this.openSocket(nextUrl);
  }

  disconnect(): void {
    this.manuallyDisconnected = true;
    this.reconnectAttempt = 0;
    this.currentUserId = null;
    this.currentToken = null;
    this.currentUrl = null;
    this.clearReconnectTimer();
    this.closeSocket();
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  protected buildDomainSocketUrl(
    domain: 'MessageManagement' | 'ConversationManagement' | 'StateManagement',
    userId: string,
    token: string
  ): string {
    const tokenParam = encodeURIComponent(token);
    const encodedUserId = encodeURIComponent(userId);
    return `${environment.wssUrl}${domain}/${encodedUserId}?token=${tokenParam}`;
  }

  protected abstract buildUrl(userId: string, token: string): string;
  protected abstract handleParsedMessage(data: any): void;

  protected handleOpen(): void {}
  protected handleClose(_event: CloseEvent): void {}
  protected handleError(_event: Event): void {}

  private openSocket(url: string): void {
    try {
      const socket = new WebSocket(url);
      this.socket = socket;

      socket.onopen = () => {
        if (this.socket !== socket) {
          socket.close();
          return;
        }

        this.reconnectAttempt = 0;
        console.log(`✅ ${this.channelName} conectado`, { url });
        this.zone.run(() => this.handleOpen());
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        if (this.socket !== socket) {
          return;
        }

        try {
          const parsed = JSON.parse(event.data);
          this.zone.run(() => this.handleParsedMessage(parsed));
        } catch (error) {
          console.error(`❌ ${this.channelName} recibió un mensaje inválido`, {
            url,
            error,
            rawData: event.data
          });
        }
      };

      socket.onerror = (event) => {
        if (this.socket !== socket) {
          return;
        }

        console.error(`❌ Error en ${this.channelName}`, { url, event });
        this.zone.run(() => this.handleError(event));
      };

      socket.onclose = (event) => {
        if (this.socket === socket) {
          this.socket = null;
        }

        console.warn(`🔌 ${this.channelName} cerrado`, {
          url,
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });

        this.zone.run(() => this.handleClose(event));

        if (!this.manuallyDisconnected && this.currentUserId && this.currentToken) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error(`❌ No fue posible abrir ${this.channelName}`, { url, error });
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.manuallyDisconnected || !this.currentUserId || !this.currentToken) {
      return;
    }

    this.clearReconnectTimer();
    this.reconnectAttempt += 1;

    const delay = Math.min(1000 * Math.max(this.reconnectAttempt, 1), 15000);
    this.reconnectTimer = window.setTimeout(() => {
      if (this.manuallyDisconnected || !this.currentUserId || !this.currentToken) {
        return;
      }

      const reconnectUrl = this.buildUrl(this.currentUserId, this.currentToken);
      this.currentUrl = reconnectUrl;
      this.openSocket(reconnectUrl);
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private closeSocket(): void {
    if (!this.socket) {
      return;
    }

    const socket = this.socket;
    this.socket = null;

    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;

    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close();
    }
  }
}

export { ScopedWebSocketBaseService };
